// The locked-unit vocabulary shared by the paywall surfaces — what a locked
// thing IS (a chapter or a matter document, described by what the plate needs),
// and the live entitlement decision about it.
//
// SPLIT OUT OF `premium-gate.tsx` because that file exports components: a
// module that exports both components and plain functions breaks React Fast
// Refresh, which is what `react(only-export-components)` is guarding. The
// grouping is honest anyway — none of this renders anything.

import { useQuery } from "convex/react";
import { api } from "@bindery/convex/_generated/api";
import { useCoarseNow } from "@/lib/use-coarse-now";
import { useLastLoaded } from "@/lib/use-last-loaded";
import type {
  CatalogBook,
  CatalogChapter,
  CatalogMatter,
} from "@bindery/shared/corpus/catalog";

export interface EntitlementStatus {
  authed: boolean;
  entitled: boolean;
  pendingOrderId: string | null;
}

/**
 * The locked thing this plate is selling. A CHAPTER OR A MATTER DOCUMENT, since
 * an epilogue can be paywalled too — described here by what the plate actually
 * needs rather than by which table it came from, so neither caller has to
 * pretend to be the other (a matter document has no chapter number to invent).
 */
export interface LockedUnit {
  /** Route segment — a chapter number as a string, or a matter slug. Doubles
   *  as the `unitKey` the paywall-impression row is counted under. */
  segment: string;
  /** The product that unlocks it: a VOLUME's, resolved at build time. */
  productId?: string;
  /** Present only for a chapter, and only so the long-standing `chapter` field
   *  of the `paywall.view` event keeps its exact shape for a chapter. */
  chapterNumber?: number;
  /** What to call it when there is no product configured to name. */
  noticeLabel: string;
}

/** Describe a locked chapter to the plate. */
export function lockedChapter(chapter: CatalogChapter): LockedUnit {
  return {
    segment: String(chapter.number),
    // THE FIX: a chapter's own volume product, not the book-wide id — so an
    // entitlement bought from another volume's cover no longer satisfies this
    // one (`model/volume.ts`'s `productIdForChapter`, threaded onto the catalog
    // as `chapter.premiumProductId`).
    productId: chapter.premiumProductId,
    chapterNumber: chapter.number,
    noticeLabel: "This chapter isn't available.",
  };
}

/** Describe a locked matter document to the plate. */
export function lockedMatter(matter: CatalogMatter): LockedUnit {
  return {
    segment: matter.slug,
    productId: matter.premiumProductId,
    noticeLabel: "This page isn't available.",
  };
}

/** What the entitlement gate concluded about the viewer, this render. */
export type GateState =
  | { kind: "loading" }
  | { kind: "entitled" }
  | { kind: "locked"; status: EntitlementStatus };

/**
 * The live entitlement decision for one locked unit — SHARED by the chapter
 * gate and the matter gate, because the subtleties below are the kind that get
 * fixed once and then quietly reintroduced by the second copy.
 *
 * THE CLOCK buys exactly one thing: `entitlementStatus` compares `now` against
 * the stored `entitledThrough`, and an all-access subscription lapses by a
 * timestamp passing rather than by a DB write, so nothing would invalidate the
 * query and the gate would never notice. The other three ways to be entitled —
 * purchase, comp, admin — are DB-driven and flip reactively regardless.
 *
 * This gate is also the ONLY thing that ends a lapsed subscriber's read:
 * `resolvePremiumAccess` deliberately evaluates at a server `Date.now()` with
 * no clock in its args, so the unit already on screen persists until the reader
 * navigates. That division of labour is intended — the server preserves the
 * current page, the gate is what shows the upsell — so re-gating only on
 * navigation is NOT the cheap win it looks like; it would hand out an unbounded
 * post-lapse read.
 *
 * HELD ACROSS THE TICK (`useLastLoaded`) because `now` is a query ARGUMENT: every
 * step it advances, Convex re-subscribes and the result goes `undefined` for a
 * round trip. Without the hold, the whole reader drops to a skeleton and back
 * once a step — which was not merely a flash, it also discarded reader-local
 * state and re-fired every mount-scoped effect, inflating `reading.open` and
 * `paywall.view` to once per tick for the premium cohort.
 *
 * The held value is scoped to a SUBJECT and an IDENTITY, and neither can change
 * under one instance: callers key the gate on the unit, and every auth
 * transition is a full-page navigation. An in-app sign-in would break that.
 */
export function useEntitlementGate(
  book: CatalogBook,
  unit: LockedUnit,
  /** The chapter a bespoke VOLUME grant would be range-tested against. A matter
   *  document passes its anchor chapter; a chapter passes its own number. */
  grantChapter: number | undefined,
): GateState {
  const now = useCoarseNow();
  const status = useLastLoaded(
    useQuery(
      api.checkout.entitlementStatus,
      unit.productId
        ? {
            productId: unit.productId,
            bookSlug: book.slug,
            ...(grantChapter !== undefined
              ? { chapterNumber: grantChapter }
              : {}),
            now,
          }
        : "skip",
    ),
  );
  if (!unit.productId) return { kind: "locked", status: NO_PRODUCT_STATUS };
  if (status === undefined) return { kind: "loading" };
  return status.entitled ? { kind: "entitled" } : { kind: "locked", status };
}

/** A unit nothing sells cannot be bought into; the plate degrades to a notice. */
const NO_PRODUCT_STATUS: EntitlementStatus = {
  authed: false,
  entitled: false,
  pendingOrderId: null,
};
